import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowLeft, FilterSearch } from "iconsax-react";

import CompanySizeDropdownField from "./CompanySizeDropdownField";
import PopupMultiSelectField, { popupFilterInputSx } from "./PopupMultiSelectField";
import LocationMultiSelectField from "./LocationMultiSelectField";
import {
  groupLocationOption,
  normalizeLocationFilterValues,
} from "../utils/locationFilters";
import { normalizeCompanySizeValue } from "../utils/companySizeOptions";

const PURPLE = "#9C4FFF";
const PURPLE_DARK = "#7C3AED";
const LABEL_BLUE = "#3451F4";

const uniq = (arr) =>
  Array.from(new Set([].concat(arr || []).filter(Boolean).map((value) => String(value).trim()).filter(Boolean)));

const cleanText = (value) => String(value ?? "").trim();

const createDefaultFilters = (initialFilters = {}) => ({
  aiAgent: cleanText(initialFilters.aiAgent),
  jobTitle: uniq(initialFilters.jobTitle),
  skills: uniq(initialFilters.skills),
  locations: normalizeLocationFilterValues(initialFilters.locations),
  owner: uniq(initialFilters.owner),
  status: uniq(initialFilters.status),
  stage: uniq(initialFilters.stage),
  source: uniq(initialFilters.source),
  candidate_details: {
    full_names: uniq(initialFilters.candidate_details?.full_names),
    first_name: cleanText(initialFilters.candidate_details?.first_name),
    last_name: cleanText(initialFilters.candidate_details?.last_name),
    email: cleanText(initialFilters.candidate_details?.email),
    phone: cleanText(initialFilters.candidate_details?.phone),
    spoken_languages: uniq(initialFilters.candidate_details?.spoken_languages),
  },
  education_experience: {
    years_experience: cleanText(initialFilters.education_experience?.years_experience),
    years_current_company: cleanText(initialFilters.education_experience?.years_current_company),
    spoken_languages: uniq(initialFilters.education_experience?.spoken_languages),
    industry: uniq(initialFilters.education_experience?.industry),
    schools: uniq(initialFilters.education_experience?.schools),
    fields_of_study: uniq(initialFilters.education_experience?.fields_of_study),
  },
  company: {
    current_company: cleanText(initialFilters.company?.current_company),
    company_size: normalizeCompanySizeValue(initialFilters.company?.company_size),
    past_companies: uniq(initialFilters.company?.past_companies),
    company_types: uniq(initialFilters.company?.company_types),
    job_functions: uniq(initialFilters.company?.job_functions),
  },
  activity_section: {
    recruitment_activity: uniq(initialFilters.activity_section?.recruitment_activity),
    tags: uniq(initialFilters.activity_section?.tags),
    past_applications: uniq(initialFilters.activity_section?.past_applications),
    notes: uniq(initialFilters.activity_section?.notes),
  },
});

function SectionTitle({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 800,
        color: LABEL_BLUE,
        fontFamily: "Be Vietnam Pro",
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

function FieldLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 700,
        color: LABEL_BLUE,
        fontFamily: "Be Vietnam Pro",
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function TextInput({ value, onChange, placeholder, inputSx }) {
  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      sx={inputSx}
    />
  );
}

function AdvancedSection({ title, children }) {
  return (
    <Box sx={{ mb: 3.2 }}>
      <SectionTitle>{title}</SectionTitle>
      <Divider sx={{ borderColor: "#93C5FD", mb: 2 }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function CandidateAdvancedSearchView({
  title = "Candidates - Advance Search",
  initialFilters,
  options = {},
  onClose,
  onApply,
}) {
  const [filters, setFilters] = useState(() => createDefaultFilters(initialFilters));

  const mergedOptions = useMemo(
    () => ({
      jobTitles: uniq(options.jobTitles),
      skills: uniq(options.skills),
      locations: normalizeLocationFilterValues(options.locations),
      owners: uniq(options.owners),
      statuses: uniq(options.statuses),
      stages: uniq(options.stages),
      sources: uniq(options.sources),
      languages: uniq(options.languages),
      industries: uniq(options.industries),
      schools: uniq(options.schools),
      fieldsOfStudy: uniq(options.fieldsOfStudy),
      companies: uniq(options.companies),
      companySizes: uniq(options.companySizes),
      companyTypes: uniq(options.companyTypes),
      jobFunctions: uniq(options.jobFunctions),
      recruitmentActivities: uniq(options.recruitmentActivities),
      tags: uniq(options.tags),
      pastApplications: uniq(options.pastApplications),
      notes: uniq(options.notes),
    }),
    [options]
  );

  const inputSx = popupFilterInputSx(PURPLE);

  const setRootField = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const setSectionField = (section, key, value) => {
    setFilters((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const clearAll = () => {
    setFilters(createDefaultFilters());
  };

  const emitApply = () => {
    onApply?.(filters);
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        backgroundColor: "#fff",
        boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #EEF2F7",
          backgroundColor: "#fff",
        }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 800,
            color: PURPLE,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {title}
        </Typography>

        <Button
          onClick={onClose}
          variant="contained"
          startIcon={<ArrowLeft size={18} color="#fff" variant="Outline" />}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            fontFamily: "Be Vietnam Pro",
            backgroundColor: PURPLE,
            boxShadow: "none",
            px: 2.2,
            "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
          }}
        >
          Back
        </Button>
      </Box>

      <Box sx={{ display: "flex", minHeight: 680 }}>
        <Box
          sx={{
            width: { xs: "100%", md: 332, lg: 332 },
            borderRight: { xs: "none", md: "1px solid #EEF2F7" },
            borderBottom: { xs: "1px solid #EEF2F7", md: "none" },
            p: 2,
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1, flexWrap: { xs: "wrap", md: "nowrap" }, rowGap: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
              <FilterSearch size={16} color={LABEL_BLUE} variant="Outline" />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: LABEL_BLUE, fontFamily: "Be Vietnam Pro" }}>
                Filter by
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={clearAll}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 11,
                  fontFamily: "Be Vietnam Pro",
                  borderColor: PURPLE,
                  color: PURPLE,
                  px: 1.6,
                  minWidth: "fit-content",
                  whiteSpace: "nowrap",
                  "&:hover": { borderColor: PURPLE_DARK, color: PURPLE_DARK, backgroundColor: "transparent" },
                }}
              >
                Clear Filters
              </Button>

              <Button
                variant="contained"
                onClick={emitApply}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 11,
                  fontFamily: "Be Vietnam Pro",
                  backgroundColor: PURPLE,
                  boxShadow: "none",
                  px: 1.6,
                  minWidth: "fit-content",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
                }}
              >
                Apply Filters
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 1.5, borderColor: "#EEF2F7" }} />

          <Stack spacing={1.5}>
            <Box>
              <FieldLabel>AI Agent:</FieldLabel>
              <TextInput
                value={filters.aiAgent}
                onChange={(value) => setRootField("aiAgent", value)}
                placeholder="Type your request"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Job Title:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.jobTitles}
                value={filters.jobTitle}
                onChange={(next) => setRootField("jobTitle", uniq(next))}
                placeholder="Type Job Title"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Skills:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.skills}
                value={filters.skills}
                onChange={(next) => setRootField("skills", uniq(next))}
                placeholder="Skill keyword anywhere on profile"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Locations:</FieldLabel>
              <LocationMultiSelectField
                options={mergedOptions.locations}
                value={filters.locations}
                onChange={(next) => setRootField("locations", normalizeLocationFilterValues(next))}
                placeholder="Select location"
                inputSx={inputSx}
                autocompleteProps={{ groupBy: groupLocationOption }}
              />
            </Box>

            <Box>
              <FieldLabel>Owner:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.owners}
                value={filters.owner}
                onChange={(next) => setRootField("owner", uniq(next))}
                placeholder="Select the owner"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Status:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.statuses}
                value={filters.status}
                onChange={(next) => setRootField("status", uniq(next))}
                placeholder="Select one or more Status"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Stage:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.stages}
                value={filters.stage}
                onChange={(next) => setRootField("stage", uniq(next))}
                placeholder="Select one or more Stages"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Source:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.sources}
                value={filters.source}
                onChange={(next) => setRootField("source", uniq(next))}
                placeholder="Select one or more Sources"
                inputSx={inputSx}
              />
            </Box>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, p: 2.5, overflow: "auto" }}>
          <AdvancedSection title="Candidate Details">
            <Box>
              <FieldLabel>First Name:</FieldLabel>
              <TextInput
                value={filters.candidate_details.first_name}
                onChange={(value) => setSectionField("candidate_details", "first_name", value)}
                placeholder="Type first name"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Last Name:</FieldLabel>
              <TextInput
                value={filters.candidate_details.last_name}
                onChange={(value) => setSectionField("candidate_details", "last_name", value)}
                placeholder="Type last name"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Email:</FieldLabel>
              <TextInput
                value={filters.candidate_details.email}
                onChange={(value) => setSectionField("candidate_details", "email", value)}
                placeholder="Type email address"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Phone Number:</FieldLabel>
              <TextInput
                value={filters.candidate_details.phone}
                onChange={(value) => setSectionField("candidate_details", "phone", value)}
                placeholder="Type phone number"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Spoken Languages:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.languages}
                value={filters.candidate_details.spoken_languages}
                onChange={(next) => setSectionField("candidate_details", "spoken_languages", uniq(next))}
                placeholder="Type languages"
                inputSx={inputSx}
              />
            </Box>

            <Box />
          </AdvancedSection>

          <AdvancedSection title="Education & Experience">
            <Box>
              <FieldLabel>Years of Experience:</FieldLabel>
              <TextInput
                value={filters.education_experience.years_experience}
                onChange={(value) => setSectionField("education_experience", "years_experience", value)}
                placeholder="Total years of work experience"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Years in current Company:</FieldLabel>
              <TextInput
                value={filters.education_experience.years_current_company}
                onChange={(value) => setSectionField("education_experience", "years_current_company", value)}
                placeholder="Years working in current company"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Spoken Languages:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.languages}
                value={filters.education_experience.spoken_languages}
                onChange={(next) => setSectionField("education_experience", "spoken_languages", uniq(next))}
                placeholder="Type languages"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Industry:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.industries}
                value={filters.education_experience.industry}
                onChange={(next) => setSectionField("education_experience", "industry", uniq(next))}
                placeholder="Industry experience"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Schools:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.schools}
                value={filters.education_experience.schools}
                onChange={(next) => setSectionField("education_experience", "schools", uniq(next))}
                placeholder="Schools attended"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Fields of Study:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.fieldsOfStudy}
                value={filters.education_experience.fields_of_study}
                onChange={(next) => setSectionField("education_experience", "fields_of_study", uniq(next))}
                placeholder="Education experience"
                inputSx={inputSx}
              />
            </Box>
          </AdvancedSection>

          <AdvancedSection title="Company">
            <Box>
              <FieldLabel>Current Company:</FieldLabel>
              <TextInput
                value={filters.company.current_company}
                onChange={(value) => setSectionField("company", "current_company", value)}
                placeholder="Company name"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Company size:</FieldLabel>
              <CompanySizeDropdownField
                value={filters.company.company_size}
                onChange={(value) => setSectionField("company", "company_size", value)}
                options={mergedOptions.companySizes}
                placeholder="50 - 200 employees"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Past Companies:</FieldLabel>
              <PopupMultiSelectField
                companyJobCounts
          options={mergedOptions.companies}
                value={filters.company.past_companies}
                onChange={(next) => setSectionField("company", "past_companies", uniq(next))}
                placeholder="Past employer"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Company Types:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.companyTypes}
                value={filters.company.company_types}
                onChange={(next) => setSectionField("company", "company_types", uniq(next))}
                placeholder="Type of company"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Job Functions:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.jobFunctions}
                value={filters.company.job_functions}
                onChange={(next) => setSectionField("company", "job_functions", uniq(next))}
                placeholder="Areas of expertise"
                inputSx={inputSx}
              />
            </Box>

            <Box />
          </AdvancedSection>

          <AdvancedSection title="Recruitment & Candidate Activity">
            <Box>
              <FieldLabel>Recruitment Activity:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.recruitmentActivities}
                value={filters.activity_section.recruitment_activity}
                onChange={(next) => setSectionField("activity_section", "recruitment_activity", uniq(next))}
                placeholder="Recruiting activity by your team"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Tags:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.tags}
                value={filters.activity_section.tags}
                onChange={(next) => setSectionField("activity_section", "tags", uniq(next))}
                placeholder="Filter by tags"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Past Applications:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.pastApplications}
                value={filters.activity_section.past_applications}
                onChange={(next) => setSectionField("activity_section", "past_applications", uniq(next))}
                placeholder="Application sources"
                inputSx={inputSx}
              />
            </Box>

            <Box>
              <FieldLabel>Notes:</FieldLabel>
              <PopupMultiSelectField
                options={mergedOptions.notes}
                value={filters.activity_section.notes}
                onChange={(next) => setSectionField("activity_section", "notes", uniq(next))}
                placeholder="Filter by notes"
                inputSx={inputSx}
              />
            </Box>
          </AdvancedSection>
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid #EEF2F7",
          backgroundColor: "#fff",
          px: 2.5,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.25,
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            fontFamily: "Be Vietnam Pro",
            backgroundColor: PURPLE,
            boxShadow: "none",
            px: 2.2,
            "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
          }}
        >
          Close
        </Button>

        <Stack direction="row" spacing={1.25}>
          <Button
            variant="outlined"
            onClick={clearAll}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              borderColor: PURPLE,
              color: PURPLE,
              px: 2.2,
              "&:hover": { borderColor: PURPLE_DARK, color: PURPLE_DARK, backgroundColor: "transparent" },
            }}
          >
            Clear Filters
          </Button>

          <Button
            variant="contained"
            onClick={emitApply}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: PURPLE,
              boxShadow: "none",
              px: 2.2,
              "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
            }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </Card>
  );
}
